// Composant interface Joueur
import {GameMap} from "./Map";
import React, {useEffect, useRef, useState} from "react";
import {socket} from "../socket";
import '../index.css'

// Fonctions utilitaires pour la persistance locale
const saveLocalState = (state) => {
    localStorage.setItem('werewolf_game_state', JSON.stringify({
        ...state,
        timestamp: Date.now()
    }));
};

const loadLocalState = () => {
    try {
        const saved = localStorage.getItem('werewolf_game_state');
        if (saved) {
            const data = JSON.parse(saved);
            const timeDiff = Date.now() - data.timestamp;

            // Ne restaurer que si récent (< 5 minutes)
            if (timeDiff < 300000) {
                return data;
            }
        }
    } catch (error) {
        console.error('Failed to load local state:', error);
    }
    return null;
};

// Fonction utilitaire pour générer une clé de sécurité unique
const generateSecurityKey = () => {
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15);
    return `${timestamp}-${randomStr}`;
};

// Set pour stocker les clés utilisées
const usedSecurityKeys = new Set();

export default function PlayerInterface() {
    // États avec persistance locale
    const [userPosition, setUserPosition] = useState(() => {
        const saved = loadLocalState();
        return saved?.userPosition || {position: {lat: 48.8566, lng: 2.3522}, name: 'Player'};
    });

    const [role, setRole] = useState(() => {
        const saved = loadLocalState();
        return saved?.role || null;
    });

    const [isGameStarted, setIsGameStarted] = useState(false);
    const [GPSPoints, setGPSPoints] = useState([])
    const [questions, setQuestions] = useState([])
    const [currentQuestions, setCurrentQuestions] = useState({})
    const [etape, setEtape] = useState(0)
    const [endDate, setEndDate] = useState(new Date())
    const [reunionAsked, setReunionAsked] = useState(false);
    const [showModal, setShowModal] = React.useState(false);
    const [showReunionModal, setShowReunionModal] = React.useState(false);
    const [showModalQuestion, setShowModalQuestion] = React.useState(false);
    const [questionModalError, setQuestionModalError] = React.useState("");
    const [questionModalSuccess, setQuestionModalSuccess] = React.useState("");
    const [isDead, setIsDead] = React.useState(false);
    const [answer, setAnswer] = React.useState(null);
    const [nextEtapePending, setNextEtapePending] = useState(true);
    const [isAnswerPending, setIsAnswerPending] = useState(false);
    const [winQuestions, setWinQuestions] = React.useState(false);
    const [name, setName] = React.useState("");
    const [wrongResponse, setWrongResponse] = React.useState(false);
    const [playersPositions, setPlayersPositions] = React.useState([]);
    const [accuracy, setAccuracy] = React.useState(0);
    const [manhunt, setManhunt] = React.useState(false);
    const [isHunted, setIsHunted] = React.useState(false);
    const [manhuntRemaining, setManhuntRemaining] = React.useState(0);
    const [nowTs, setNowTs] = React.useState(Date.now());
    const alarmRef = useRef(null);
    const sirenIntervalRef = useRef(null);
    let audioContext;

    const [showEndGameModal, setShowEndGameModal] = useState(false);
    const [endGameMessage, setEndGameMessage] = useState("");
    const [endGameResult, setEndGameResult] = useState({loup: false, timeIsUp: false});
    const [countdown, setCountdown] = useState(5);

    // Sauvegarder automatiquement les changements d'état
    useEffect(() => {
        saveLocalState({
            userPosition,
            role,
            playerName: userPosition.name || name
        });
    }, [userPosition, role, name]);

    // Heartbeat pour maintenir la connexion
    useEffect(() => {
        const heartbeat = setInterval(() => {
            if (socket.connected) {
                socket.emit('heartbeat', {
                    playerId: socket.id,
                    timestamp: Date.now()
                });
            }
        }, 30000);

        return () => clearInterval(heartbeat);
    }, []);

    // Gestion de la visibilité de la page (détection de mise en veille)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && !socket.connected) {
                console.log('Page became visible, checking connection');
                socket.connect();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    function playBeep() {
        if (!audioContext) {
            audioContext = new (window.AudioContext || window.webkitAudioContext)();
        }

        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = 800;
        gainNode.gain.value = 0.5;

        oscillator.start();
        setTimeout(() => oscillator.stop(), 200);
    }

    // Sirène de secours (Web Audio) si le fichier mp3 ne peut pas être joué
    function startSirenFallback() {
        if (sirenIntervalRef.current) return;
        sirenIntervalRef.current = setInterval(() => playBeep(), 700);
    }

    function stopSirenFallback() {
        if (sirenIntervalRef.current) {
            clearInterval(sirenIntervalRef.current);
            sirenIntervalRef.current = null;
        }
    }

    function startAlarm() {
        try {
            if (!alarmRef.current) {
                alarmRef.current = new Audio('/sirene.mp3');
                alarmRef.current.loop = true;
            }
            const p = alarmRef.current.play();
            if (p && p.catch) p.catch(() => startSirenFallback());
        } catch (e) {
            startSirenFallback();
        }
    }

    function stopAlarm() {
        if (alarmRef.current) {
            alarmRef.current.pause();
            alarmRef.current.currentTime = 0;
        }
        stopSirenFallback();
    }

    function askQuestion() {
        if (showModalQuestion === false) {
            setShowModalQuestion(true);
        }
    }

    useEffect(() => {
        socket.on('game_ended', (result) => {
            console.log("Partie terminée:", result);
            setEndGameResult(result);
            setEndGameMessage(result.message || "La partie est terminée");
            setShowEndGameModal(true);
            stopAlarm();
            setManhunt(false);
            setIsHunted(false);

            // Nettoyer l'état local
            localStorage.removeItem('werewolf_game_state');

            // Démarrer un compte à rebours
            let count = 5;
            setCountdown(count);

            const intervalId = setInterval(() => {
                count--;
                setCountdown(count);

                if (count <= 0) {
                    clearInterval(intervalId);
                    window.location.href = '/';
                }
            }, 1000);

            return () => clearInterval(intervalId);
        });

        return () => {
            socket.off('game_ended');
        };
    }, []);

    useEffect(() => {
        console.log("gamestarted ? ", isGameStarted)
        console.log("is dead ? ", isDead)
        let intervalAudio
        const onBeforeUnload = (ev) => {
            socket.disconnect();
        };

        window.addEventListener("beforeunload", onBeforeUnload);

        socket.emit('get-game-info');
        socket.on('game-info', (gameInfo) => {
            console.log('game infos');
            console.log(gameInfo);
            setIsGameStarted(gameInfo.isGameStarted);
            setEndDate(gameInfo.endDate);
            setReunionAsked(gameInfo.reunionAsked);
            if (gameInfo.manhunt !== undefined) setManhunt(gameInfo.manhunt);
            socket.emit('get-user-info');
        });

        // Mode chasse à l'homme : tous les écrans passent en alerte rouge
        socket.on('manhunt-started', (data) => {
            setManhunt(true);
            if (data && data.endDate) setEndDate(data.endDate);
        });

        // Le joueur traqué : alarme sonore continue
        socket.on('manhunt-hunted', (data) => {
            setManhunt(true);
            setIsHunted(true);
            if (data && data.endDate) setEndDate(data.endDate);
            if (data && typeof data.remaining === 'number') setManhuntRemaining(data.remaining);
            startAlarm();
        });

        socket.on('reunion-asked', () => {
            setShowReunionModal(true);
        });

        socket.on('reunion-ended', () => {
            setShowReunionModal(false);
        })

        socket.emit('get-user-info');
        socket.on('user-info', (userInfo) => {
            if (userInfo) {
                console.log("userInfos: ", userInfo);
                setIsDead(!userInfo.alive);
                if (!userInfo.alive) stopAlarm();
                setEtape(userInfo.etape);
                console.log('definition des points gps: ', userInfo.gpsPoints);
                setGPSPoints(userInfo.gpsPoints);
                setName(userInfo.name);
            }
        });

        socket.on('role_assigned', ({role}) => {
            console.log(`Vous êtes ${role}`);
            setRole(role);
            setShowModal(true)
        });

        socket.on('gps_assigned', ({gpsPoints}) => {
            console.log(`Point GPS : ${gpsPoints}`);
            setGPSPoints(gpsPoints);
        });

        socket.on('questions', ({questions}) => {
            console.log(`Questions : ${questions}`);
            setQuestions(questions);
        });

        let q = {question: ''};
        socket.on('ask-question', (question) => {
            if (currentQuestions.question !== question.question && question.question !== q.question) {
                q = question;
                setQuestionModalSuccess("");

                setNextEtapePending(true);
                setIsAnswerPending(false);
                setCurrentQuestions(question);
                // alert("recu " +  JSON.stringify(question));
                // alert("possédé " + JSON.stringify(currentQuestions));
                askQuestion();
            }
        });

        socket.on('wrong-answer', () => {
            if (wrongResponse === false) {
                setQuestionModalError("Mauvaise réponse");
                setIsAnswerPending(false);
                // setShowModalQuestion(false);
                setWrongResponse(true);
                setTimeout(() => {
                    setQuestionModalError("");
                    setWrongResponse(false);
                    setIsAnswerPending(false);
                }, 3000);
            }
        });

        socket.on('next-etape', () => {
            setQuestionModalSuccess("Réponse correcte !");
            setNextEtapePending(false);
            setIsAnswerPending(false);
            setShowModalQuestion(false);
            socket.emit('get-game-info');
        });

        socket.on('answer-result', () => {
            setIsAnswerPending(false);
        });

        socket.on('player-win', () => {
            setWinQuestions(true);
            socket.emit('get-game-info');
        });

        socket.on('all-positions', (positions) => {
            setPlayersPositions(positions);
        });

        socket.on('game-state-restore', (data) => {
            console.log('Game state restored:', data);
            if (data.userInfo) {
                setIsDead(!data.userInfo.alive);
                setEtape(data.userInfo.etape);
                setGPSPoints(data.userInfo.gpsPoints);
                setName(data.userInfo.name);
            }
            if (data.gameInfo) {
                setIsGameStarted(data.gameInfo.isGameStarted);
                setEndDate(data.gameInfo.endDate);
                setReunionAsked(data.gameInfo.reunionAsked);
            }
        });

        // Activation de la géolocalisation avec gestion d'erreur améliorée
        const watchId = navigator.geolocation.watchPosition(
            (position) => {
                const locationData = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                    timestamp: new Date().toISOString()
                };
                 if(position.coords.latitude !== null && position.coords.longitude !== null){
                     setAccuracy(position.coords.accuracy);
                     socket.emit('update-position', locationData);

                     const newPosition = {
                         position: {lat: position.coords.latitude, lng: position.coords.longitude},
                         name: 'Ma position'
                     };
                     setUserPosition(newPosition);

                     // Sauvegarder la position
                     saveLocalState({
                         userPosition: newPosition,
                         role,
                         playerName: name
                     });
                 }
            },
            (error) => {
                console.error('Erreur de géolocalisation:', error);
                // Afficher une notification à l'utilisateur selon le type d'erreur
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        console.warn("Géolocalisation refusée par l'utilisateur");
                        break;
                    case error.POSITION_UNAVAILABLE:
                        console.warn("Position indisponible");
                        break;
                    case error.TIMEOUT:
                        console.warn("Timeout de géolocalisation");
                        break;
                }
            },
            {
                timeout: 10000,
                enableHighAccuracy: true,
                maximumAge: 5000 // Accepter une position de moins de 5 secondes
            }
        );

        if (isGameStarted && role !== "loup") {
            intervalAudio = setInterval(() => {
                playBeep();
            }, 10000); /// 10 secondes
        }

        if (role === "loup") {
            clearInterval(intervalAudio);
        }

        return () => {
            navigator.geolocation.clearWatch(watchId);
            socket.off('role_assigned');
            socket.off('game-info');
            socket.off('gps_assigned');
            socket.off('player-win');
            socket.off('next-etape');
            socket.off('answer-result');
            socket.off('all-positions');
            socket.off('game-state-restore');
            socket.off('wrong-answer');
            socket.off('ask-question');
            socket.off('reunion-asked');
            socket.off('reunion-ended');
            socket.off('manhunt-started');
            socket.off('manhunt-hunted');
            window.removeEventListener("beforeunload", onBeforeUnload);
            clearInterval(intervalAudio);
        };
    }, [role, isGameStarted, isDead]);

    // Couper l'alarme au démontage du composant
    useEffect(() => {
        return () => stopAlarm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Tick chaque seconde pour le compte à rebours (auto-géré, pas de dépendance externe)
    useEffect(() => {
        const id = setInterval(() => setNowTs(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    const formatCountdown = () => {
        if (!endDate) return '--:--:--';
        const end = new Date(endDate).getTime();
        if (isNaN(end)) return '--:--:--';
        let s = Math.max(0, Math.floor((end - nowTs) / 1000));
        const h = String(Math.floor(s / 3600)).padStart(2, '0');
        s %= 3600;
        const m = String(Math.floor(s / 60)).padStart(2, '0');
        const sec = String(s % 60).padStart(2, '0');
        return `${h}:${m}:${sec}`;
    };

    const answerRef = useRef();

    const handleValidate = () => {
        const value = answerRef.current?.value.trim();
        if (!value || isAnswerPending) {
            return;
        }
        const securityKey = generateSecurityKey();

        setIsAnswerPending(true);
        // Stocker la clé localement pour éviter les doublons côté client
        usedSecurityKeys.add(securityKey);

        socket.emit('check-answer', {
            answer: value,
            key: securityKey
        });
    };


    // Fonction pour forcer la récupération des coordonnées GPS avec retry
    const handleForceGPSUpdate = () => {
        if (navigator.geolocation) {
            const attemptGPS = (attempt = 1) => {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const locationData = {
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                            accuracy: position.coords.accuracy,
                            timestamp: new Date().toISOString()
                        };

                        socket.emit('update-position', locationData);

                        const newPosition = {
                            position: {lat: position.coords.latitude, lng: position.coords.longitude},
                            name: 'Ma position'
                        };
                        setUserPosition(newPosition);

                        // Sauvegarder la position
                        saveLocalState({
                            userPosition: newPosition,
                            role,
                            playerName: name
                        });
                        alert(`Position GPS mise à jour avec succès (précision: ${Math.round(position.coords.accuracy)}m)`);
                    },
                    (error) => {
                        console.error('Erreur GPS:', error);
                        if (attempt < 3) {
                            console.log(`Tentative ${attempt + 1}/3 dans 2 secondes...`);
                            setTimeout(() => attemptGPS(attempt + 1), 2000);
                        } else {
                            alert('Impossible de récupérer la position GPS après 3 tentatives');
                        }
                    },
                    {
                        timeout: 15000,
                        enableHighAccuracy: true,
                        maximumAge: 0
                    }
                );
            };

            attemptGPS();
        } else {
            alert('La géolocalisation n\'est pas supportée par ce navigateur');
        }
    };

    return (
        <div className={manhunt
            ? "min-h-screen bg-gradient-to-b from-red-950 via-red-900 to-black p-4 manhunt-pulse"
            : "min-h-screen bg-gradient-to-b from-gray-900 via-purple-900 to-gray-900 p-4"}>
            <div className="max-w-7xl mx-auto">

                {manhunt && (
                    <div className="bg-gradient-to-r from-red-700 to-red-900 rounded-lg shadow-2xl border-4 border-red-500 p-6 mb-6 text-center manhunt-blink">
                        <h2 className="text-3xl font-extrabold text-white tracking-widest">
                            🚨 ALERTE ROUGE — CHASSE À L'HOMME 🚨
                        </h2>
                        {isHunted ? (
                            <p className="mt-3 text-lg text-red-100 font-semibold">
                                Vous êtes le dernier survivant ! Réalisez TOUS les points GPS restants
                                ({manhuntRemaining > 0 ? `${manhuntRemaining} à faire` : 'en cours'}) avant
                                la fin du temps, sans vous faire tuer. Les loups vous traquent !
                            </p>
                        ) : (
                            <p className="mt-3 text-lg text-red-100 font-semibold">
                                Le dernier villageois est traqué. Loups : trouvez-le. Villageois : il doit
                                terminer tous les points pour vous faire gagner !
                            </p>
                        )}
                    </div>
                )}

                <div className="bg-gray-800 bg-opacity-90 rounded-lg shadow-2xl border-2 border-purple-700 p-6 mb-6">
                    <div className="text-center">
                        {/*<h1 className="text-3xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-purple-400 to-red-500">*/}
                        {/*    🌙 Loup-Garou de Thiercelieux 🐺*/}
                        {/*</h1>*/}
                        {role && (
                            <div
                                className="inline-block bg-gradient-to-r from-purple-900 to-red-900 px-6 py-3 rounded-lg border-2 border-purple-500 shadow-lg">
                                <span className="text-2xl font-bold text-purple-200">
                                    {role === 'loup' ? '🐺' : role === 'villageois' ? '🏠' :
                                        role === 'chasseur' ? '🏹' : '👧'} Mon rôle : {role}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {isGameStarted && (
                    <div
                        className="bg-gray-800 bg-opacity-90 rounded-lg shadow-2xl border-2 border-purple-700 p-6 mb-6">
                        <div className="text-center">
                            <span className="text-2xl font-bold text-purple-300 block mb-2">⏰ Temps restant</span>
                            <span className="text-4xl font-bold text-red-400 block mb-3">
                                {formatCountdown()}
                            </span>
                            <div className="inline-block bg-gray-900 px-4 py-2 rounded-lg border border-purple-600">
                                <span className="text-lg text-gray-300">
                                    📍 Précision GPS: <span
                                    className="text-green-400 font-semibold">{accuracy.toFixed(2)}m</span>
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {isGameStarted && (role !== "loup") && GPSPoints && GPSPoints.length > 0 && (
                    <div className="mb-6 rounded-lg overflow-hidden border-2 border-purple-700 shadow-2xl">
                        <GameMap players={[userPosition, ...playersPositions]} isGameMaster={false}
                                 markers={[GPSPoints[etape]]}/>
                    </div>
                )}

                {!isGameStarted && (
                    <div
                        className="bg-gray-800 bg-opacity-90 rounded-lg shadow-2xl border-2 border-yellow-600 p-8 text-center">
                        <h3 className="text-2xl font-bold text-yellow-400">⏳ La partie n'a pas encore commencé</h3>
                        <p className="text-gray-300 mt-2">Attendez que le maître du jeu démarre la partie...</p>
                    </div>
                )}

                {isGameStarted && (role !== "loup") && !isDead && !reunionAsked && (
                    <div className="space-y-4">
                        <button
                            className="w-full py-4 px-6 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg transition shadow-lg transform hover:scale-105 font-bold text-lg hover:from-green-700 hover:to-green-800"
                            onClick={() => socket.emit('reunion-asked')}
                        >
                            👥 Demander une réunion
                        </button>
                        <button
                            className="w-full py-4 px-6 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg transition shadow-lg transform hover:scale-105 font-bold text-lg hover:from-red-700 hover:to-red-800"
                            onClick={() => socket.emit('dead', {name})}
                        >
                            ☠️ Je suis mort
                        </button>
                    </div>
                )}

                {isGameStarted && !isDead && (
                    <button
                        className="w-full mt-4 py-4 px-6 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg transition shadow-lg transform hover:scale-105 font-bold text-lg hover:from-orange-700 hover:to-orange-800"
                        onClick={handleForceGPSUpdate}
                    >
                        📍 Forcer mise à jour GPS
                    </button>
                )}

                {isGameStarted && isDead && (
                    <div
                        className="bg-gradient-to-r from-red-900 to-gray-900 rounded-lg shadow-2xl border-2 border-red-600 p-8 text-center">
                        <span className="text-3xl font-bold text-red-400 block">☠️ Vous êtes mort ☠️</span>
                        <p className="text-gray-300 mt-3 text-lg">Vous ne pouvez plus rien faire...</p>
                    </div>
                )}

                {!socket.connected && (
                    <div
                        className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-6 py-3 rounded-full shadow-2xl border-2 border-red-400 animate-pulse z-50">
                        <span className="font-bold">⚠️ Connexion perdue - Tentative de reconnexion...</span>
                    </div>
                )}

                {showEndGameModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80">
                        <div
                            className="bg-gradient-to-b from-gray-800 to-gray-900 p-8 rounded-lg shadow-2xl max-w-md w-full border-4 border-purple-600">
                            <h2 className={`text-3xl font-bold mb-4 text-center ${endGameResult.loup ? 'text-red-400' : 'text-green-400'}`}>
                                {endGameResult.loup ? '🐺 Les loups ont gagné!' : '🏠 Les villageois ont gagné!'}
                            </h2>

                            <p className="mb-6 text-gray-300 text-center text-lg">{endGameMessage}</p>

                            {role && (
                                <div className="mb-4 p-4 bg-gray-700 rounded-lg border border-purple-600">
                                    <p className="font-semibold text-purple-300">Votre rôle: {role}</p>
                                    <p className={isDead ? 'text-red-400' : 'text-green-400'}>
                                        {isDead ? '☠️ Vous êtes mort' : '❤️ Vous avez survécu'}
                                    </p>
                                </div>
                            )}

                            <div className="text-center mt-4">
                                <p className="text-sm text-gray-400">
                                    Retour à l'écran d'accueil dans <span
                                    className="font-bold text-purple-400">{countdown}</span> secondes...
                                </p>

                                <button
                                    onClick={() => window.location.href = '/'}
                                    className="mt-4 px-6 py-3 bg-gradient-to-r from-red-600 to-purple-600 text-white rounded-lg hover:from-red-700 hover:to-purple-700 font-semibold shadow-lg transform hover:scale-105 transition"
                                >
                                    Retour immédiat
                                </button>
                            </div>
                        </div>
                    </div>)
                }
                {showModalQuestion ? (
                    <>
                        <div
                            className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
                            <div className="relative w-auto my-6 mx-auto max-w-3xl">
                                <div
                                    className="border-4 border-purple-700 rounded-lg shadow-2xl relative flex flex-col w-full bg-gradient-to-b from-gray-800 to-gray-900 outline-none focus:outline-none">
                                    <div
                                        className="flex items-start justify-between p-5 border-b border-solid border-purple-700 rounded-t">
                                        <h3 className="text-3xl font-semibold text-purple-300">
                                            ❓ Question {etape + 1}
                                        </h3>
                                    </div>
                                    <div className="relative p-6 flex-auto">
                                        <p className="my-4 text-gray-200 text-lg leading-relaxed">
                                            {currentQuestions.question}
                                        </p>
                                    </div>
                                    <div
                                        className="flex items-center justify-center p-6 border-t border-solid border-purple-700 rounded-b gap-3">
                                        <input
                                            ref={answerRef}
                                            type="text"
                                            className="flex-1 text-center bg-gray-700 text-gray-200 border-2 border-purple-600 rounded-lg py-4 px-3 focus:outline-none focus:border-purple-400"
                                            placeholder="Votre réponse"
                                            disabled={isAnswerPending}
                                        />

                                        <button
                                            className="bg-gradient-to-r from-green-600 to-green-700 text-white font-bold px-6 py-3 rounded-lg shadow-lg hover:from-green-700 hover:to-green-800 outline-none focus:outline-none transition transform hover:scale-105"
                                            type="button"
                                            onClick={handleValidate}
                                            disabled={isAnswerPending}
                                        >
                                            ✅ Valider
                                        </button>
                                    </div>
                                    <div>
                                        {questionModalError && questionModalError.length > 0 && (
                                            <span className="text-red-600">
                                                {questionModalError}
                                            </span>
                                        )}

                                        {questionModalSuccess && questionModalSuccess.length > 0 && (
                                            <span className="text-green-600">
                                                {questionModalSuccess}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="opacity-80 fixed inset-0 z-40 bg-black"></div>
                    </>
                ) : null}

                {showModal ? (
                    <>
                        <div
                            className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
                            <div className="relative w-auto my-6 mx-auto max-w-3xl">
                                <div
                                    className="border-4 border-purple-700 rounded-lg shadow-2xl relative flex flex-col w-full bg-gradient-to-b from-gray-800 to-gray-900 outline-none focus:outline-none">
                                    <div
                                        className="flex items-start justify-between p-5 border-b border-solid border-purple-700 rounded-t">
                                        <h3 className="text-3xl font-semibold text-purple-300">
                                            🎭 Votre rôle
                                        </h3>
                                    </div>
                                    <div className="relative p-6 flex-auto">
                                        <img src={role + ".png"} alt={role}
                                             className="mx-auto rounded-lg shadow-lg border-2 border-purple-600"/>
                                        <p className="my-4 text-gray-200 text-lg leading-relaxed text-center">
                                            Vous êtes : <span className="font-bold text-purple-300">{role}</span>
                                        </p>
                                    </div>
                                    <div
                                        className="flex items-center justify-center p-6 border-t border-solid border-purple-700 rounded-b">
                                        <button
                                            className="bg-gradient-to-r from-green-600 to-green-700 text-white font-bold px-6 py-3 rounded-lg shadow-lg hover:from-green-700 hover:to-green-800 outline-none focus:outline-none transition transform hover:scale-105"
                                            type="button"
                                            onClick={() => setShowModal(false)}
                                        >
                                            ✅ C'est noté !
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="opacity-80 fixed inset-0 z-40 bg-black"></div>
                    </>
                ) : null}

                {winQuestions ? (
                    <>
                        <div
                            className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
                            <div className="relative w-auto my-6 mx-auto max-w-3xl">
                                <div
                                    className="border-4 border-green-600 rounded-lg shadow-2xl relative flex flex-col w-full bg-gradient-to-b from-gray-800 to-gray-900 outline-none focus:outline-none">
                                    <div
                                        className="flex items-start justify-between p-5 border-b border-solid border-green-600 rounded-t">
                                        <h3 className="text-3xl font-semibold text-green-400">
                                            Vous avez terminé la partie !
                                        </h3>
                                    </div>
                                    <div className="relative p-6 flex-auto">
                                        <p className="my-4 text-gray-200 text-lg leading-relaxed">
                                            Vous avez trouvés tous les points et répondus correctement à toutes les
                                            questions !
                                            Vous devez maintenant attendre que les autres joueurs terminent la partie
                                            pour
                                            remporter la victoire.
                                            <br/><br/>
                                            <span className="text-green-400 font-bold">Vous ne pouvez plus être tué par les loups.</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="opacity-80 fixed inset-0 z-40 bg-black"></div>
                    </>
                ) : null}

                {showReunionModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4">
                        <div className="relative w-full max-w-2xl">
                            <div
                                className="border-4 border-purple-700 rounded-3xl shadow-2xl bg-gradient-to-b from-gray-900 to-black">
                                <div className="flex items-center justify-between px-6 py-4 border-b border-purple-800">
                                    <h3 className="text-3xl font-semibold text-purple-200">
                                        📯 Demande de réunion !
                                    </h3>
                                </div>
                                <div className="px-6 py-8 text-gray-200 text-lg leading-relaxed">
                                    <p className="my-4 text-blueGray-500 text-lg leading-relaxed">
                                        Une réunion a été demandée, rendez vous au point de rassemblement !
                                    </p>
                                </div>
                                {/*footer*/}
                                <div
                                    className="flex items-center justify-center p-6 border-t border-solid border-blueGray-200 rounded-b">
                                    <button
                                        className="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                                        type="button"
                                        onClick={() => setShowReunionModal(false)}
                                    >
                                        C'est noté !
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* CSS pour l'indicateur de connexion */}
                <style jsx>{`
                    .connection-indicator {
                        position: fixed;
                        bottom: 20px;
                        left: 50%;
                        transform: translateX(-50%);
                        padding: 10px 20px;
                        border-radius: 20px;
                        font-size: 14px;
                        font-weight: bold;
                        z-index: 1000;
                    }

                    .connection-indicator.offline {
                        background-color: #ff4444;
                        color: white;
                        animation: pulse 2s infinite;
                    }

                    @keyframes pulse {
                        0% {
                            opacity: 1;
                        }
                        50% {
                            opacity: 0.5;
                        }
                        100% {
                            opacity: 1;
                        }
                    }

                    .manhunt-blink {
                        animation: manhuntBlink 1s infinite;
                    }

                    @keyframes manhuntBlink {
                        0%, 100% {
                            box-shadow: 0 0 30px 5px rgba(255, 0, 0, 0.9);
                        }
                        50% {
                            box-shadow: 0 0 10px 2px rgba(255, 0, 0, 0.3);
                        }
                    }

                    .manhunt-pulse {
                        animation: manhuntBg 1.5s infinite;
                    }

                    @keyframes manhuntBg {
                        0%, 100% {
                            filter: brightness(1);
                        }
                        50% {
                            filter: brightness(1.25);
                        }
                    }
                `}</style>
            </div>
        </div>
    );
}